"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Eye, EyeOff, Check, Wifi, WifiOff } from "lucide-react";
import { useAuthStore, MOCK_USERS } from "@/stores/auth-store";
import { isApiReachable } from "@/lib/api/http-client";
import type { UserRole } from "@/lib/rbac";
import { BackendStatusDebug } from "@/components/auth/backend-status-debug";

// const ROLE_LABELS: Record<UserRole, string> = {
//   owner: 'Owner',
//   admin: 'Admin',
//   marketing: 'Marketing',
//   cs_agent: 'CS Agent',
//   finance: 'Finance',
//   developer: 'Developer',
// };

export default function LoginPage() {
  const router = useRouter();
  const t = useTranslations("auth");
  const tc = useTranslations("common");
  const { login, isAuthenticated, isLoading, error: authError, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [apiStatus, setApiStatus] = useState<"checking" | "online" | "offline">("checking");

  // Check API status on mount
  useEffect(() => {
    isApiReachable().then((ok) => setApiStatus(ok ? "online" : "offline"));
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      router.push("/dashboard");
    }
  }, [isAuthenticated, router]);

  // Sync auth store error
  useEffect(() => {
    if (authError) {
      setError(authError);
      clearError();
    }
  }, [authError, clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const success = await login(email, password);
    if (success) {
      router.push("/dashboard");
    } else if (!authError) {
      setError("Email atau kata sandi salah");
    }
  };

  // const handleQuickLogin = async (mockEmail: string) => {
  //   setEmail(mockEmail);
  //   setPassword('password');
  //   setError('');
  //   const success = await login(mockEmail, 'password');
  //   if (success) {
  //     router.push('/dashboard');
  //   }
  // };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-1 flex">
        {/* Backend Status Debug Panel (Dev only) */}
        {/* <BackendStatusDebug /> */}

        {/* Left Panel: Branding (Hidden on mobile) */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-purple-600 p-12 flex-col justify-between text-white">
          {/* Logo */}
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <span className="text-2xl font-bold">K</span>
              </div>
              <span className="text-2xl font-bold">Kustomer Suite</span>
            </div>
          </div>

          {/* Feature Highlights */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold mb-4">AI-Powered Customer Engagement Platform</h2>
              <p className="text-lg text-white/90">
                Manage WhatsApp, Telegram, and Instagram conversations in one place with intelligent automation.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4" />
                </div>
                <span className="text-white/90">Unified inbox across all channels</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4" />
                </div>
                <span className="text-white/90">AI chatbot with 80% automation</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4" />
                </div>
                <span className="text-white/90">Real-time campaign monitoring</span>
              </div>
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <p className="text-sm italic mb-3 text-white/90">
              &ldquo;Kustomer Suite helped us reduce response time by 70% and handle 5x more customers with the same
              team.&rdquo;
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-sm font-semibold">AP</span>
              </div>
              <div>
                <p className="text-sm font-semibold">Ahmad Pratama</p>
                <p className="text-xs text-white/70">CEO, PT Digital Solusi</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel: Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-8 bg-white">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-2">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-xl font-bold text-white">K</span>
                </div>
                <span className="text-xl font-bold text-gray-900">Kustomer Suite</span>
              </div>
            </div>

            {/* Header */}
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{t("welcomeBack")}</h1>
              <p className="text-gray-600">{t("signInSubtitle")}</p>
            </div>

            {/* API Status Indicator */}
            {/* <div className={`mb-4 px-3 py-2 rounded-lg flex items-center gap-2 text-xs font-medium ${
            apiStatus === 'online' 
              ? 'bg-green-50 border border-green-200 text-green-800'
              : apiStatus === 'offline'
              ? 'bg-orange-50 border border-orange-200 text-orange-800'
              : 'bg-gray-50 border border-gray-200 text-gray-600'
          }`}>
            {apiStatus === 'online' ? (
              <>
                <Wifi className="w-3.5 h-3.5" />
                <span>API Backend Terhubung</span>
              </>
            ) : apiStatus === 'offline' ? (
              <>
                <WifiOff className="w-3.5 h-3.5" />
                <span>API Offline — Mode Mock Aktif</span>
              </>
            ) : (
              <>
                <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <span>Memeriksa koneksi API...</span>
              </>
            )}
          </div> */}

            {/* Quick Login (Dev Mode) */}
            {/*
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs font-semibold text-amber-800 mb-2">
              {t('selectRole')} (Dev Mode)
              {apiStatus === 'online' && <span className="ml-2 text-green-600">— password: &quot;password&quot;</span>}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {MOCK_USERS.map(mockUser => (
                <button
                  key={mockUser.id}
                  onClick={() => handleQuickLogin(mockUser.email)}
                  disabled={isLoading}
                  className="px-2 py-1.5 bg-white border border-amber-300 rounded text-xs font-medium text-amber-900 hover:bg-amber-100 transition-colors truncate disabled:opacity-50"
                  title={`${mockUser.name} (${mockUser.email})`}
                >
                  {ROLE_LABELS[mockUser.role]}
                </button>
              ))}
            </div>
          </div>
          */}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-900">{error}</p>
              </div>
            )}

            {/* Social Login */}
            <div className="space-y-3 mb-6">
              <button className="w-full h-12 px-4 border-2 border-gray-300 rounded-lg flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-700">{t("continueGoogle")}</span>
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">{t("orContinueWith")}</span>
                </div>
              </div>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t("emailLabel")}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("emailPlaceholder")}
                  required
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg text-sm focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                />
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-900">{t("passwordLabel")}</label>
                  <Link href="/forgot-password" className="text-sm font-medium text-blue-600 hover:text-blue-700">
                    {t("forgotPassword")}
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("passwordPlaceholder")}
                    required
                    className="w-full h-12 px-4 pr-12 border border-gray-300 rounded-lg text-sm focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-100"
                  />
                  <span className="text-sm text-gray-700">{t("rememberMe")}</span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-blue-600 text-white rounded-lg text-base font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>{t("signingIn")}</span>
                  </>
                ) : (
                  t("signIn")
                )}
              </button>
            </form>

            {/* Sign Up Link */}
            <p className="text-center text-sm text-gray-600 mt-8">
              {t("noAccount")}
              <Link href="/signup" className="font-medium text-blue-600 hover:text-blue-700 ml-1">
                {t("startTrial")}
              </Link>
            </p>

            {/* Terms & Privacy */}
            <p className="text-center text-xs text-gray-500 mt-6">
              {t("termsAgree")}{" "}
              <Link href="/terms" className="text-gray-700 hover:underline">
                {t("termsOfService")}
              </Link>{" "}
              {tc("and")}{" "}
              <Link href="/privacy" className="text-gray-700 hover:underline">
                {t("privacyPolicy")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
